import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
} from '@mui/material';
import {
  Car,
  Plane,
  Home,
  ShoppingCart,
  HeartPulse,
  Briefcase,
  UtensilsCrossed,
  Landmark,
  Dumbbell,
  TriangleAlert,
  ExternalLink,
} from 'lucide-react';
import Reveal from './Reveal';
import { tokens } from '../theme';

const industries = [
  {
    name: 'Automotive',
    pain: '52% of inquiries arrive outside business hours',
    solution: 'AI helps answer test-drive and pricing questions instantly, with your oversight.',
    icon: Car,
    source: 'Keepme Study',
    sourceUrl: 'https://www.keepme.ai/case-studies/how-many-gym-leads-are-lost-after-hours/',
  },
  {
    name: 'Tourism & Travel',
    pain: '78% of travelers book with the first responder',
    solution: 'Respond to trip inquiries in seconds with personalized package info.',
    icon: Plane,
    source: 'Chili Piper',
    sourceUrl: 'https://www.chilipiper.com/article/speed-to-lead-statistics',
  },
  {
    name: 'Real Estate',
    pain: '62% of inquiries arrive after hours & weekends',
    solution: 'AI helps qualify buyers and schedule viewings while you focus on closings.',
    icon: Home,
    source: 'AgentZap',
    sourceUrl: 'https://agentzap.ai/blog/real-estate-lead-statistics',
  },
  {
    name: 'E-commerce',
    pain: '55% abandon if they can\'t find quick answers',
    solution: 'AI helps answer sizing, shipping, and stock questions to drive purchases.',
    icon: ShoppingCart,
    source: 'Forrester Research',
    sourceUrl: 'https://www.powerreviews.com/missing-product-questions-answers-raises-shopper-suspicions/',
  },
  {
    name: 'Healthcare',
    pain: '61% of patients skip visits due to scheduling hassles',
    solution: 'AI helps handle appointment inquiries and basic FAQs around the clock.',
    icon: HeartPulse,
    source: 'Notable Health',
    sourceUrl: 'https://www.notablehealth.com/blog/notable-survey-61-of-patients-skip-medical-appointments-due-to-scheduling-hassles',
  },
  {
    name: 'Professional Services',
    pain: 'Avg B2B response time is 42 hours — too slow',
    solution: 'AI helps capture client details and schedule calls from any messaging platform.',
    icon: Briefcase,
    source: 'Chili Piper',
    sourceUrl: 'https://www.chilipiper.com/article/speed-to-lead-statistics',
  },
  {
    name: 'Restaurants & Bars',
    pain: 'Unanswered peak-hour calls cost ~$23k a year in lost tables',
    solution: 'AI answers calls and messages, books tables straight into your calendar — even at Friday-night peak.',
    icon: UtensilsCrossed,
    source: 'Slang.ai',
    sourceUrl: 'https://www.slang.ai/post/restaurant-phone-statistics',
  },
  {
    name: 'Government & Public Services',
    pain: '85% of citizens expect business-grade service from public offices',
    solution: 'AI answers permits, hours, and program FAQs in any language — staff time goes to the real cases.',
    icon: Landmark,
    source: 'Accenture',
    sourceUrl: 'https://www.accenture.com/us-en/insights/public-service/citizen-experience',
  },
  {
    name: 'Fitness & Wellness',
    pain: 'Up to half of new-member leads arrive after the desk closes',
    solution: 'AI captures trial sign-ups and books classes or sessions the moment someone asks.',
    icon: Dumbbell,
    source: 'Keepme Study',
    sourceUrl: 'https://www.keepme.ai/case-studies/how-many-gym-leads-are-lost-after-hours/',
  },
];

export default function IndustriesSection() {
  return (
    <Box id="industries" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Reveal sx={{ textAlign: 'center', mb: 7 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
            Industries
          </Typography>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Built for Businesses With Customers
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 560, mx: 'auto' }}
          >
            Every industry has its own front-office work. CleoLead does yours.
          </Typography>
        </Reveal>

        <Grid container spacing={3}>
          {industries.map((industry, index) => {
            const Icon = industry.icon;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={industry.name}>
                <Reveal delay={index * tokens.motion.stagger} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      p: 3.5,
                      borderRadius: `${tokens.radiusCard}px`,
                      bgcolor: tokens.bgPaper,
                      border: `1px solid ${tokens.border}`,
                      boxShadow: tokens.shadowSoft,
                      height: '100%',
                      transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                      cursor: 'default',
                      '&:hover': {
                        borderColor: '#D9CDBC',
                        boxShadow: tokens.shadowLift,
                      },
                    }}
                  >
                    {/* icon + name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: `${tokens.radiusControl}px`,
                          bgcolor: tokens.copperTint,
                          color: tokens.copper,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={22} strokeWidth={1.5} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {industry.name}
                      </Typography>
                    </Box>

                    {/* warning stat — warm amber tint */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        px: 1.5,
                        py: 1.2,
                        borderRadius: `${tokens.radiusControl}px`,
                        bgcolor: 'rgba(180, 83, 9, 0.08)',
                        border: '1px solid rgba(180, 83, 9, 0.18)',
                        color: tokens.warning,
                        mb: 2,
                      }}
                    >
                      <Box sx={{ mt: '2px', display: 'flex', flexShrink: 0 }}>
                        <TriangleAlert size={14} strokeWidth={1.5} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: tokens.warning,
                          fontWeight: 500,
                          lineHeight: 1.5,
                        }}
                      >
                        {industry.pain}
                      </Typography>
                    </Box>

                    <Link
                      href={industry.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                        mb: 1.5,
                        transition: `color ${tokens.motion.micro}ms ease`,
                        '&:hover': { color: tokens.copper },
                      }}
                    >
                      <ExternalLink size={12} strokeWidth={1.5} />
                      {industry.source}
                    </Link>

                    {/* solution */}
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                    >
                      {industry.solution}
                    </Typography>
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
